import { memo, useMemo } from "react"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAlarmClock, faCalendarClock, faTag, faWheat, faUserLock } from "@fortawesome/pro-regular-svg-icons"
import { faBadgePercent, faCalendarExclamation } from "@fortawesome/pro-solid-svg-icons"
import { Box, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import type { ProductItem, GroupItem, ProductGroupAvailability, ProductGroupSecurity } from "../../productsTypes"

const useStyles = makeStyles()(() => ({
    iconBox: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".375rem",
        padding: ".125rem .375rem",
        borderRadius: ".25rem",
        border: `1px solid ${tailwindPalette.slate[200]}`,
    },
    icon: {
        fontSize: ".75rem",
        color: tailwindPalette.slate[500],
    },
    tooltipTitle: {
        fontWeight: 600,
        fontSize: ".75rem",
    },
    tooltipValue: {
        fontSize: ".75rem",
        whiteSpace: "pre-line",
    },
    tooltipSection: {
        "&:not(:last-child)": {
            marginBottom: ".375rem",
        },
    },
}))

type InfoIcon = Readonly<{
    icon: IconDefinition
    label: string
    value?: string
}>

function useProductInfoIcons(product: ProductItem): ReadonlyArray<InfoIcon> {
    const { l10n } = useAppLocalization()

    return useMemo(() => {
        const icons: InfoIcon[] = []

        if (product.deadline) {
            icons.push({
                icon: faAlarmClock,
                label: l10n.getString("product-info-icon-deadline-label"),
                value: l10n.getString("product-info-icon-deadline-value", {
                    daysBefore: product.deadline.purchases.daysBefore,
                    timeOfDay: product.deadline.purchases.timeOfDay,
                }),
            })
        }

        if (product.availabilityDetails?.dateInterval?.from || product.availabilityDetails?.dateInterval?.to) {
            const from = product.availabilityDetails.dateInterval?.from ?? ""
            const to = product.availabilityDetails.dateInterval?.to ?? ""
            icons.push({
                icon: faCalendarClock,
                label: l10n.getString("product-info-icon-availability-label"),
                value: from && to
                    ? `${from} – ${to}`
                    : from || to,
            })
        }

        if (product.offerDetails?.discount?.type && product.offerDetails.discount.type !== "NONE") {
            icons.push({
                icon: faBadgePercent,
                label: l10n.getString("product-info-icon-offer-label"),
            })
        }

        if (product.productDescription?.marketingTag) {
            icons.push({
                icon: faTag,
                label: l10n.getString("product-info-icon-marketing-tag-label"),
                value: product.productDescription.marketingTag,
            })
        }

        if (product.productDescription?.allergens?.length) {
            icons.push({
                icon: faWheat,
                label: l10n.getString("product-info-icon-allergens-label"),
                value: product.productDescription.allergens.map((a) => a.name).join(", "),
            })
        }

        if (product.availabilityDetails?.allowPreOrder) {
            icons.push({
                icon: faCalendarExclamation,
                label: l10n.getString("product-info-icon-allow-pre-order-label"),
            })
        }

        return icons
    }, [product, l10n])
}

type ProductGroupIconData = Readonly<{
    availabilityDetails?: ProductGroupAvailability
    securitySettings?: ProductGroupSecurity
}>

function useProductGroupInfoIcons(data: ProductGroupIconData): ReadonlyArray<InfoIcon> {
    const { l10n } = useAppLocalization()

    return useMemo(() => {
        const icons: InfoIcon[] = []

        if (data.securitySettings?.isLocked) {
            icons.push({
                icon: faUserLock,
                label: l10n.getString("product-info-icon-pin-code-label"),
            })
        }

        if (data.availabilityDetails) {
            const days = data.availabilityDetails.weekDays
                .map((day) => l10n.getStringForEnum("product-group-form-week-day", day, day))
                .join(", ")
            const time = `${data.availabilityDetails.timeInterval.start} – ${data.availabilityDetails.timeInterval.end}`
            icons.push({
                icon: faCalendarClock,
                label: l10n.getString("product-info-icon-availability-label"),
                value: `${days}\n${time}`,
            })
        }

        return icons
    }, [data, l10n])
}

type InfoIconsBoxProps = Readonly<{
    icons: ReadonlyArray<InfoIcon>
}>

const InfoIconsBox = memo(function InfoIconsBox({ icons }: InfoIconsBoxProps) {
    const { classes } = useStyles()

    if (icons.length === 0) return null

    const tooltipContent = (
        <Box>
            {icons.map((entry) => (
                <Box key={entry.label} className={classes.tooltipSection}>
                    <Typography className={classes.tooltipTitle}>{entry.label}</Typography>
                    {entry.value && (
                        <Typography className={classes.tooltipValue}>
                            {entry.value}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    )

    return (
        <Tooltip title={tooltipContent} arrow placement="bottom-start">
            <Box className={classes.iconBox}>
                {icons.map((entry) => (
                    <FontAwesomeIcon key={entry.label} icon={entry.icon} className={classes.icon} />
                ))}
            </Box>
        </Tooltip>
    )
})

type ProductInfoIconsProps = Readonly<{
    product: ProductItem
}>

export const ProductInfoIcons = memo(function ProductInfoIcons({ product }: ProductInfoIconsProps) {
    const icons = useProductInfoIcons(product)
    return <InfoIconsBox icons={icons} />
})

type ProductGroupInfoIconsProps = Readonly<{
    group: GroupItem
}>

export const ProductGroupInfoIcons = memo(function ProductGroupInfoIcons({ group }: ProductGroupInfoIconsProps) {
    const data: ProductGroupIconData = {
        availabilityDetails: group.groupAvailabilityDetails,
        securitySettings: group.groupSecuritySettings,
    }
    const icons = useProductGroupInfoIcons(data)
    return <InfoIconsBox icons={icons} />
})

type ProductGroupInfoIconsStandaloneProps = Readonly<{
    availabilityDetails?: ProductGroupAvailability
    securitySettings?: ProductGroupSecurity
}>

export const ProductGroupInfoIconsStandalone = memo(function ProductGroupInfoIconsStandalone(
    props: ProductGroupInfoIconsStandaloneProps,
) {
    const icons = useProductGroupInfoIcons(props)
    return <InfoIconsBox icons={icons} />
})
